import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Mail, MapPin, ArrowUpRight } from "lucide-react";

const footerLinks = {
  Services: [
    { label: "AIOps Frameworks", href: "/services/aiops-framework" },
    { label: "Platform Engineering", href: "/services/multi-cloud-platform-engineering" },
    { label: "Infrastructure Automation", href: "/services/infrastructure-automation" },
    { label: "Cloud Migration", href: "/services/cloud-migration" },
  ],
  Technology: [
    { label: "AWS", href: "/tech/aws" },
    { label: "Azure", href: "/tech/azure" },
    { label: "GCP", href: "/tech/gcp" },
    { label: "Kubernetes", href: "/tech/kubernetes" },
    { label: "Terraform", href: "/tech/terraform" },
    { label: "AIOps", href: "/tech/aiops" },
  ],
  Company: [
    { label: "About Us", href: "/about" },
    { label: "Case Studies", href: "/#cases" },
    { label: "Insights", href: "/#insights" },
    { label: "Contact", href: "/#contact" },
  ],
};

const Logo = () => (
  <Link to="/" className="flex items-center gap-2">
    <div className="relative w-9 h-9">
      <span className="absolute top-0 left-1 w-5 h-5 rounded-full bg-[#6B9FFF] opacity-90" />
      <span className="absolute top-1 left-3 w-5 h-5 rounded-full bg-[#16C47F] opacity-90 mix-blend-screen" />
      <span className="absolute top-3 left-0 w-5 h-5 rounded-full bg-[#F06A3E] opacity-90 mix-blend-screen" />
    </div>
    <span className="font-extrabold text-lg tracking-tight text-white">NSTI</span>
  </Link>
);

export default function Footer() {
  return (
    <footer className="nst-navy-deep text-white">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Logo />
            <p className="mt-5 text-white/60 leading-relaxed max-w-sm">
              Principal-level engineers designing and operating intelligent cloud platforms on AWS, Azure, and GCP.
            </p>
            <div className="mt-6 space-y-2.5 text-sm">
              <div className="flex items-center gap-2.5 text-white/70">
                <MapPin className="w-4 h-4 text-[#6B9FFF]" /> Austin, TX
              </div>
              <a href="mailto:kween.baker@nstinstitute.com" className="flex items-center gap-2.5 text-white/70 hover:text-[#6B9FFF] break-all">
                <Mail className="w-4 h-4 text-[#6B9FFF] flex-shrink-0" /> kween.baker@nstinstitute.com
              </a>
              <a href="https://linkedin.com/in/kweenbaker" target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-white/70 hover:text-[#6B9FFF]">
                <Linkedin className="w-4 h-4 text-[#6B9FFF]" /> linkedin.com/in/kweenbaker
              </a>
            </div>
          </div>

          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title} className="md:col-span-2">
              <h4 className="text-xs uppercase tracking-wider text-white/50 font-bold mb-5">{title}</h4>
              <ul className="space-y-3">
                {links.map((l) => (
                  <li key={l.label}>
                    {l.href.startsWith("/#") ? (
                      <a href={l.href} className="text-sm text-white/80 hover:text-[#6B9FFF] transition-colors">
                        {l.label}
                      </a>
                    ) : (
                      <Link to={l.href} className="text-sm text-white/80 hover:text-[#6B9FFF] transition-colors">
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-wider text-white/50 font-bold mb-5">Get Started</h4>
            <a href="/#contact" className="inline-flex items-center gap-2 px-5 py-3 bg-[#2E6BF6] hover:bg-[#1E55D6] rounded-full text-sm font-semibold transition-colors">
              Start a Project <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-sm text-white/50">
            © {new Date().getFullYear()} NSTI — Nano Science and Technology, Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-white/50">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
